"use client";

import { Fragment } from "react";
import type { ArticleHeroComponent, ArticleHeroContent } from "../../types";
import { RuntimeImage, RuntimeLink, useLocale } from "../../runtime/RendererRuntime";
import { translateCategoryLabel } from "../../i18n/categories";
import { t } from "../../i18n/strings";
import { resolveLinkHref } from "../../utils/links";
import { formatDisplayDate, toIsoDate } from "../../utils/formatDisplayDate";
import { getImageObjectPositionStyle, getImageOverrideProps, mergeClassNames, mergeStyles, resolveImageValue } from "../../utils/imageOptions";
import { ArticleHeroShare } from "./ArticleHeroShare";

/**
 * Normalize one baked SUBJECT breadcrumb for display. The projector emits the category trail as
 * `?category=` filter links whose labels are the FRENCH category titles; we keep the filter link
 * but translate the label from the static category map (categories are code-first reference data,
 * so their labels translate in code, not per-field).
 *
 * "Articles" is NOT a category — it's the all-articles listing, prepended as a fixed root crumb by
 * the renderer (see {@link ArticleHeroRenderer}). Any legacy `communique-presse` crumb still riding
 * along in older baked data is dropped here (returns null) so it never doubles up with that root.
 */
function normalizeCrumb(
  crumb: { label: string; href?: unknown },
  locale: string,
): { label: string; href: string | undefined } | null {
  const resolved = resolveLinkHref(crumb.href as never) as string | undefined;
  const keyPath = resolved?.match(/[?&]category=([^&#]+)/)?.[1];
  if (keyPath === "communique-presse") {
    return null;
  }
  const label = translateCategoryLabel(locale, keyPath ?? crumb.label);
  return { label, href: resolved };
}

/** Up-to-two initials for the byline fallback avatar: first + last word
 *  initial (e.g. "Nioni Masela" → "NM"), or the first two letters of a
 *  single-word name. Empty string when there is no usable name. */
function getAuthorInitials(name?: string): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Round byline avatar. Renders the author portrait when picture data is
 *  present (`authorImage`); otherwise a brand-blue disc with the author's
 *  initials, so every article shows a consistent, polished byline. */
function AuthorAvatar({ name, image }: { name: string; image?: ArticleHeroContent["authorImage"] }) {
  const resolved = image ? resolveImageValue(image) : { src: "" };
  const src = resolved.src;
  const initials = getAuthorInitials(name);
  return (
    <div
      className="relative grid h-11 w-11 shrink-0 place-items-center overflow-hidden rounded-full bg-gradient-to-b from-[#0069B4] to-[#005CA9] text-white ring-2 ring-white shadow-[0_2px_8px_-2px_rgba(0,92,169,0.45)]"
      aria-hidden="true"
    >
      {src ? (
        <RuntimeImage
          src={src}
          alt={name}
          width={44}
          height={44}
          className="h-full w-full object-cover"
        />
      ) : (
        <span className="text-[15px] font-bold leading-none tracking-wide">{initials}</span>
      )}
    </div>
  );
}

/** Article hero renderer with breadcrumb, image, author and share links. */
export function ArticleHeroRenderer({ component }: { component: ArticleHeroComponent }) {
  const locale = useLocale();
  const {
    eyebrow,
    breadcrumbs = [],
    title,
    author,
    authorRole,
    authorImage,
    publishDate,
    featuredImage,
  } = component.content;
  const { src, options } = resolveImageValue(featuredImage.src);
  const objectPositionStyle = getImageObjectPositionStyle(options);
  const overrideProps = getImageOverrideProps(options);
  const fitImageContainerStyle: React.CSSProperties = { minHeight: "min(50vmin, 280px)" };
  // Mobile gets a taller frame than md+ (client review 07/2026: the portrait
  // image read as a thumbnail on phones).
  const mobileImageContainerStyle: React.CSSProperties = { minHeight: "min(80vmin, 420px)" };

  // Racine du fil alignée sur la navigation : les articles vivent sous le groupe
  // « Publications » du menu, donc le fil lit « Publications / Articles » — chaque
  // miette est un vrai lien. La CATÉGORIE n'apparaît PAS dans le fil : elle
  // doublonnerait mot pour mot le sur-titre ambre juste en dessous (retour client
  // 02/08) — c'est le sur-titre, cliquable, qui porte le lien vers le listing filtré.
  const subjectCrumbs = breadcrumbs
    .map((crumb) => normalizeCrumb(crumb, locale))
    .filter((c): c is { label: string; href: string | undefined } => c !== null);
  const displayCrumbs: { label: string; href: string | undefined }[] = [
    { label: t(locale, "publicationsCrumb"), href: "/publications" },
    { label: translateCategoryLabel(locale, "communique-presse"), href: "/articles" },
  ];

  const hasContext = Boolean(eyebrow || displayCrumbs.length > 0);

  // Sur-titre ambre du gabarit sujet : le DOMAINE de l'article (sa catégorie),
  // déjà traduit — même repère orange uppercase que les pages-sujets, et CLIQUABLE
  // vers le listing filtré (« Tous les articles Monnaie et paiements »). Un
  // `eyebrow` saisi dans le contenu garde la priorité (sans lien) ; sans catégorie
  // ni eyebrow, rien.
  const domainCrumb = subjectCrumbs[subjectCrumbs.length - 1];
  const domainEyebrow = eyebrow || domainCrumb?.label || "";
  const domainHref = eyebrow ? undefined : domainCrumb?.href;

  return (
    // bcc-article-hero: below lg the host CSS pulls the blue panel flush against the
    // screen edges and the below-header bar (globals.css) — no page gutter on
    // mobile/tablet, so rounding only starts at lg.
    <section className="bcc-article-hero bg-[#EDF7FC] pb-6 pt-6 mb-6 md:mb-0 md:pt-10 lg:pt-12 lg:pb-10 lg:rounded-xl px-4 md:px-6 lg:px-14">
      {/* Pas de plafond interne : le contenu du hero s'aligne sur la largeur du corps
          d'article en dessous (le conteneur de page borne déjà à 75rem). */}
      <div className="rounded-3xl">
        {/* 2 columns from md instead of lg */}
        {/* Le texte prend l'espace restant ; la colonne image est bornée sur tablette
            (280-300px) mais devient fluide sur desktop (~40% du hero, plancher 360px)
            pour ne pas laisser de vide inutile quand le titre est court. */}
        <div className="grid gap-6 md:gap-8 md:grid-cols-[minmax(0,1fr)_minmax(280px,300px)] lg:grid-cols-[minmax(0,1fr)_minmax(360px,40%)] md:items-stretch">
          {/* TEXT COLUMN */}
          <div className="space-y-4">
          {/* MÊME grammaire de fil que l'en-tête de sujet (SubjectHeader) : séparateur
              « / », liens bleu marque avec soulignement au survol, corps text-sm
              semibold gris ardoise — les deux familles de pages ne divergent plus. */}
          {hasContext && displayCrumbs.length > 0 && (
            <nav
              aria-label={t(locale, "breadcrumbAriaLabel")}
              className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-500"
            >
              {displayCrumbs.map((display, index) => {
                return (
                  <span key={`${display.label}-${index}`} className="flex items-center gap-2">
                    {index > 0 && <span aria-hidden="true">/</span>}
                    {display.href ? (
                      <RuntimeLink href={display.href} className="text-[#005CA9] hover:underline">
                        {display.label}
                      </RuntimeLink>
                    ) : (
                      <span>{display.label}</span>
                    )}
                  </span>
                );
              })}
            </nav>
          )}

          {/* Bloc sur-titre + titre + filet, au MÊME gabarit que l'en-tête de sujet :
              sur-titre ambre uppercase (le domaine de l'article), titre bleu marque
              Aleo gras à interligne serré (mt-2 sous le sur-titre, comme SubjectHeader),
              filet ambre à mt-4. Les tailles sont UN CRAN sous le gabarit sujet
              (retour client 02/08 : « too much ») — un titre d'article court sur
              trois ou quatre lignes, l'échelle pleine écrasait le hero. Le gabarit
              sujet étant descendu à 32px sur mobile (03/08), le cran d'écart est
              maintenu ici : 28px, sinon les deux niveaux devenaient indiscernables. */}
          <div>
            {domainEyebrow ? (
              domainHref ? (
                <RuntimeLink
                  href={domainHref}
                  className="inline-block text-xs font-bold uppercase tracking-[0.18em] text-[#E98A00] hover:underline"
                >
                  {domainEyebrow}
                </RuntimeLink>
              ) : (
                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#E98A00]">
                  {domainEyebrow}
                </p>
              )
            ) : null}
            <h1
              className={`text-[1.75rem] font-bold leading-tight text-[#005CA9] md:text-[2.15rem] lg:text-[2.5rem] lg:leading-[1.15] md:max-w-[28ch] ${domainEyebrow ? "mt-2" : ""}`}
              style={{ fontFamily: "var(--font-aleo)" }}
            >
              {title}
            </h1>
            <div className="mt-4 h-1 w-12 rounded-sm bg-[#FFA401]" aria-hidden="true" />
          </div>

          {/* MOBILE IMAGE: between title and author (more padding from title) */}
          <div className="mt-6 block md:hidden">
            <div
              className={mergeClassNames(
                "relative w-full overflow-hidden rounded border border-[#E1E9F5]",
                overrideProps.className
              )}
              style={mergeStyles(mobileImageContainerStyle, overrideProps.style)}
            >
              <RuntimeImage
                src={src}
                alt={featuredImage.alt}
                className="absolute inset-0 h-full w-full object-cover"
                style={objectPositionStyle}
                priority
              />
            </div>
          </div>

          {/* Summary is intentionally not rendered in the hero — design shows title → author
              → date → share buttons with no description block. The data is preserved in the
              CMS payload so cards/listings/SEO can still consume it. */}

          {/* Demande client (revue 07/2026) : l'auteur (nom, rôle, avatar) n'apparaît JAMAIS sur
              le site public — il reste visible uniquement dans l'admin. Les données restent dans
              le payload CMS ; seule la date de publication est affichée ici. */}
          <div className="flex items-center gap-3 text-sm xl:text-base text-[#4B5675]">
            <div className="min-w-0">
              {publishDate ? (
                // <time datetime> carries the machine-readable date (crawlers, readers) behind the
                // same visible French date; `dateTime` is omitted when the value is not a date.
                <p className="mt-0.5 font-medium text-[#0F2849]">
                  <time dateTime={toIsoDate(publishDate) ?? undefined}>{formatDisplayDate(publishDate, locale)}</time>
                </p>
              ) : null}
            </div>
          </div>

            <ArticleHeroShare title={title} />
          </div>

          {/* IMAGE COLUMN ON MD+:
              - Below the title (because title is full-width above the grid)
              - To the right of summary/author/social */}
          <div className="hidden md:flex justify-center md:justify-end md:items-stretch">
            <div
              className={mergeClassNames(
                "relative w-full overflow-hidden rounded border border-[#E1E9F5] h-full",
                overrideProps.className
              )}
              style={mergeStyles(fitImageContainerStyle, overrideProps.style)}
            >
              <RuntimeImage
                src={src}
                alt={featuredImage.alt}
                className="absolute inset-0 h-full w-full object-cover"
                style={objectPositionStyle}
                priority
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
